import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody, TableCell,
  TableHead, TablePagination, TableRow, TextField, Typography,
} from "@material-ui/core";
import { useNotify } from "react-admin";
import { cloError, cloGet, cloPatch, cloPost, ENDPOINTS } from "./api";
import MerchantPicker from "./MerchantPicker";

const GroupsTab: React.FC = () => {
  const notify = useNotify();
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState<any | null>(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [addId, setAddId] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await cloGet(ENDPOINTS.CLO_MERCHANT_GROUPS, { page: page + 1, pageSize, search: search || undefined });
      setRows(res.data || []);
      setTotal(res.pagination?.total || 0);
    } catch (e) {
      notify(cloError(e), "warning");
    }
  }, [page, pageSize, search, notify]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const open = async (id: number) => {
    try {
      const res = await cloGet(`${ENDPOINTS.CLO_MERCHANT_GROUPS}/${id}`);
      setGroup(res.data);
      setForm({ name: res.data.name, description: res.data.description || "" });
      setAddId("");
    } catch (e) {
      notify(cloError(e), "warning");
    }
  };

  const openCreate = () => {
    setGroup({});
    setForm({ name: "", description: "" });
  };

  const save = async () => {
    try {
      const body = { name: form.name, description: form.description || null };
      const res = group.id
        ? await cloPatch(`${ENDPOINTS.CLO_MERCHANT_GROUPS}/${group.id}`, body)
        : await cloPost(ENDPOINTS.CLO_MERCHANT_GROUPS, body);
      notify("Group saved", "info");
      load();
      open(res.data.id);
    } catch (e) {
      notify(cloError(e), "warning");
    }
  };

  const members = async (body: object, message: string) => {
    try {
      await cloPost(ENDPOINTS.CLO_MERCHANT_ACTIONS, { ...body, groupId: group.id });
      notify(message, "info");
      load();
      open(group.id);
    } catch (e) {
      notify(cloError(e), "warning");
    }
  };

  return (
    <Paper>
      <Box p={2} display="flex" alignItems="center" style={{ gap: 16 }}>
        <TextField label="Search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
        <Box flexGrow={1} />
        <Button color="primary" variant="contained" onClick={openCreate}>Add group</Button>
      </Box>
      <Box px={2}>
        <Typography variant="body2" color="textSecondary">
          A group is a hand-picked set of shops, e.g. Supermarkets. Campaigns use a group as their competitor set, so a missing shop puts its customers in the wrong cohort.
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Members</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} hover>
              <TableCell>{r.name}</TableCell>
              <TableCell>{r.description || "—"}</TableCell>
              <TableCell>{r.member_count}</TableCell>
              <TableCell align="right"><Button size="small" color="primary" onClick={() => open(r.id)}>Open</Button></TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={4} align="center">No groups</TableCell></TableRow>}
        </TableBody>
      </Table>
      <TablePagination
        component="div" count={total} page={page} rowsPerPage={pageSize}
        onChangePage={(_, p) => setPage(p)}
        onChangeRowsPerPage={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
      />

      <Dialog disableBackdropClick open={group !== null} onClose={() => setGroup(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{group?.id ? group.name : "Add group"}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="dense" label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField fullWidth margin="dense" label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Box textAlign="right">
            <Button size="small" color="primary" disabled={!form.name.trim()} onClick={save}>{group?.id ? "Save details" : "Create"}</Button>
          </Box>
          {group?.id && (
            <>
              <Typography variant="subtitle1">Members</Typography>
              <Box my={1}>
                {group.members.map((m: any) => (
                  <Chip key={m.id} size="small" label={m.name} style={{ margin: 2 }}
                    onDelete={() => members({ action: "remove_group_member", cloMerchantId: m.id }, `${m.name} removed`)} />
                ))}
                {group.members.length === 0 && <Typography variant="body2" color="textSecondary">No members yet</Typography>}
              </Box>
              <MerchantPicker label="Add merchant" value={addId} onChange={setAddId} />
              <Box textAlign="right">
                <Button size="small" color="primary" disabled={!addId}
                  onClick={() => { members({ action: "add_group_members", cloMerchantIds: [Number(addId)] }, "Merchant added"); setAddId(""); }}>
                  Add to group
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroup(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default GroupsTab;
